import React, { useMemo } from 'react';
import styled from 'styled-components';

// Local components
import { Preview, PreviewWrapper, CFLogo, Watermark } from './components';

type ImageProps = {
  src: string;
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.lightBlue};
  height: 100%;
  width: 100%;
  padding: 7.5em 1.5em 1.5em;
  color: ${({ theme }) => theme.colors.darkBlue};
  overflow-y: scroll;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  padding-top: 4.75em;
  gap: 1em;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 1.5em;
  gap: 1.5em;
`;

const Image = styled.div<ImageProps>`
  position: absolute;
  top: 3.75em;
  margin: 0 auto;
  width: 7.5em;
  height: 7.5em;
  background-image: ${({ src }) => `url('${src}')`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 100%;
  border: 0.5em solid ${({ theme }) => theme.colors.white};
  box-sizing: border-box;
`;

const Heading = styled.p`
  font-weight: 700;
  font-size: 2em;
`;

const Greeting = styled.p`
  font-size: 0.75em;
  word-wrap: break-word;
  max-width: 80%;
`;

const Description = styled.p`
  font-size: 0.625em;
  width: 100%;
`;

type Props = {
  imageUrl?: string;
  heading?: string;
  greeting?: string;
  description?: string;
  fullSize?: boolean;
};

export function GenericCertificatePreview({
  imageUrl,
  heading,
  greeting,
  description,
  fullSize = false
}: Props) {
  return useMemo(
    () => (
      <PreviewWrapper fullSize={fullSize}>
        <Preview fullSize={fullSize} padding={false}>
          <Wrapper>
            <Content>
              {imageUrl && <Image src={imageUrl} />}
              {heading && <Heading>{heading}</Heading>}
              {greeting && (
                <Greeting>
                  {greeting.split('\n').map((item, index) => {
                    return (
                      <span key={index}>
                        {item}
                        <br />
                      </span>
                    );
                  })}
                </Greeting>
              )}
            </Content>
            <Footer>
              <CFLogo />
              {description && <Description>{description}</Description>}
            </Footer>
            <Watermark isVisible={fullSize} />
          </Wrapper>
        </Preview>
      </PreviewWrapper>
    ),
    [description, fullSize, greeting, heading, imageUrl]
  );
}
